import {
  type CellKeyDownEvent,
  type ValueSetterParams,
} from "ag-grid-community";
import { type CustomCellRendererProps } from "ag-grid-react";
import { ChevronIcon } from "vega-icons";

import { getFullName } from "@vega-api/custom/utils/user";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { NdaTrackingStatus } from "@vega-api/generated/types/ndaTrackingStatus";
import { CounterBadge } from "@vega-core/components/CounterBadge/CounterBadge";
import { DropdownCellEditor } from "@vega-core/components/DataGrid/DropdownCellEditor";
import { useColumnToggle } from "@vega-core/components/DataGrid/useColumnToggle";
import { suppressActionsTabKey } from "@vega-core/components/DataGrid/utils";
import { type ThemedDropdownOptionType } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { EMPTY_CELL_VALUE } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";

import { TIER_OPTIONS } from "../../../constants/prospectConstants";
import {
  deriveExportColumns,
  type ExportableColDef,
  toGridColumns,
} from "../../../hooks/deriveExportColumns";
import { RejectDetailsTooltip } from "../../TrackingTable/components/RejectDetailsTooltip";
import { TrackingRowActionsMenu } from "../../TrackingTable/components/TrackingRowActionsMenu";
import {
  formatLimitedPartnerCellText,
  formatStageCellText,
  formatTierCellText,
} from "../../TrackingTable/utils/formatCellContent";
import {
  hasIssuedNda,
  NDA_STATUS_LABEL,
  NDA_STATUS_ORDER,
  NdaStatusContent,
  resolveNdaStatus,
} from "../components/ndaStatusDisplay";
import { NrlCell } from "../components/NrlCell";
import { OpportunityOwnerCell } from "../components/OpportunityOwnerCell";

type UseTrackProcessColumnsParams = {
  dealId: string;
  canEditDealOpportunity: boolean;
  isMutationLocked: boolean;
};

export const TRACK_PROCESS_COL_IDS = {
  limitedPartnerName: "limitedPartnerName",
  actions: "actions",
  stage: "stage",
  tier: "tier",
  nda: "nda",
  nrl: "nrl",
  opportunityOwner: "opportunityOwner",
} as const;

const renderTierBadge = (option: ThemedDropdownOptionType) => (
  <CounterBadge
    className="bg-background/tertiary"
    count={option.value}
    variant="rounded"
  />
);

const renderNdaStatusOption = (option: ThemedDropdownOptionType) => (
  <NdaStatusContent status={option.value as NdaTrackingStatus} />
);

export const useTrackProcessColumns = ({
  dealId,
  canEditDealOpportunity,
  isMutationLocked,
}: UseTrackProcessColumnsParams) => {
  const limitedPartnerNameColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> =
    {
      colId: TRACK_PROCESS_COL_IDS.limitedPartnerName,
      headerName: m.coInvestments_tracking_limitedPartner(),
      field: "limitedPartnerName",
      width: 240,
      pinned: "left",
      exportValue: formatLimitedPartnerCellText,
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
        if (!data) return null;

        return (
          <span
            className="body-base-medium"
            data-testid={`cell-${data.dealOpportunityId}-limitedPartnerName`}
          >
            {data.limitedPartnerName}
          </span>
        );
      },
    };

  const actionsColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> = {
    colId: TRACK_PROCESS_COL_IDS.actions,
    headerName: "",
    width: 50,
    pinned: "left",
    sortable: false,
    suppressKeyboardEvent: suppressActionsTabKey,
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
      if (!data) return null;

      return (
        <TrackingRowActionsMenu
          dealId={dealId}
          showConfirmNdaSigned={false}
          trackingItem={data}
        />
      );
    },
  };

  const stageColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> = {
    colId: TRACK_PROCESS_COL_IDS.stage,
    headerName: m.coInvestments_tracking_stage(),
    width: 140,
    toggleable: true,
    field: "stage",
    exportValue: formatStageCellText,
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
      if (!data) return null;

      return (
        <RejectDetailsTooltip
          gpRejectReason={data.gpRejectReason}
          gpRejectSubreason={data.gpRejectSubreason}
          rejectComment={data.rejectComment}
          rejectReason={data.rejectReason}
        >
          <span className="body-base-regular">{formatStageCellText(data)}</span>
        </RejectDetailsTooltip>
      );
    },
  };

  const tierColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> = {
    colId: TRACK_PROCESS_COL_IDS.tier,
    headerName: m.coInvestments_tracking_tier(),
    width: 100,
    toggleable: true,
    field: "tier",
    exportValue: formatTierCellText,
    editable: () => canEditDealOpportunity,
    cellEditor: DropdownCellEditor,
    cellEditorPopup: true,
    cellEditorPopupPosition: "under",
    cellEditorParams: {
      options: TIER_OPTIONS,
      renderOption: renderTierBadge,
    },
    valueGetter: (params) => params.data?.tier ?? "",
    valueSetter: (
      params: ValueSetterParams<DealTrackingLimitedPartnerBreakdown, string>,
    ) => {
      if (params.newValue == null || params.newValue === params.data.tier) {
        return false;
      }

      params.data.tier = params.newValue;

      return true;
    },
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
      if (!data) return null;

      return (
        <CounterBadge
          className="bg-background/tertiary"
          count={data.tier}
          variant="rounded"
        />
      );
    },
  };

  // SIGNED is reconciled with reality and read-only, so it's never editable once reached;
  // selecting SIGNED routes through a confirmation modal in useTrackProcessEdit.
  const ndaOptions: ThemedDropdownOptionType[] = NDA_STATUS_ORDER.map(
    (status) => ({ value: status, label: NDA_STATUS_LABEL[status] }),
  );

  const isNdaEditable = (status: NdaTrackingStatus) =>
    canEditDealOpportunity && status !== NdaTrackingStatus.SIGNED;

  const ndaColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> = {
    colId: TRACK_PROCESS_COL_IDS.nda,
    headerName: m.coInvestments_tracking_nda(),
    width: 160,
    toggleable: true,
    sortable: false,
    exportValue: (row) =>
      hasIssuedNda(row)
        ? NDA_STATUS_LABEL[resolveNdaStatus(row)]
        : EMPTY_CELL_VALUE,
    editable: (params) =>
      hasIssuedNda(params.data) && isNdaEditable(resolveNdaStatus(params.data)),
    cellEditor: DropdownCellEditor,
    cellEditorPopup: true,
    cellEditorPopupPosition: "under",
    cellEditorParams: {
      options: ndaOptions,
      renderOption: renderNdaStatusOption,
    },
    valueGetter: (params) => resolveNdaStatus(params.data),
    valueSetter: (
      params: ValueSetterParams<
        DealTrackingLimitedPartnerBreakdown,
        NdaTrackingStatus
      >,
    ) => {
      const current = resolveNdaStatus(params.data);

      if (params.newValue == null || params.newValue === current) return false;

      params.data.ndaTrackingStatus = params.newValue;

      return true;
    },
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
      if (!data) return null;

      if (!hasIssuedNda(data)) {
        return (
          <span data-testid={`nda-cell-${data.dealOpportunityId}`}>
            {EMPTY_CELL_VALUE}
          </span>
        );
      }

      const status = resolveNdaStatus(data);

      return (
        <div
          className="flex items-center gap-1 h-full"
          data-testid={`nda-cell-${data.dealOpportunityId}`}
        >
          <NdaStatusContent status={status} />
          {isNdaEditable(status) ? (
            <ChevronIcon className="size-4 text-text/secondary" />
          ) : null}
        </div>
      );
    },
  };

  const nrlColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> = {
    colId: TRACK_PROCESS_COL_IDS.nrl,
    headerName: m.coInvestments_track_process_nrl(),
    width: 120,
    toggleable: true,
    sortable: false,
    exportValue: (row) =>
      `${row.numberOfNrlSigned ?? 0}/${row.numberOfNrlTracked ?? 0}`,
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) =>
      data ? (
        <NrlCell canEdit={canEditDealOpportunity} dealId={dealId} row={data} />
      ) : null,
  };

  const opportunityOwnerColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> =
    {
      colId: TRACK_PROCESS_COL_IDS.opportunityOwner,
      headerName: m.coInvestments_prospects_opportunityOwner(),
      width: 370,
      toggleable: true,
      sortable: false,
      exportValue: (row) => {
        const owner = row.dealOpportunityOwner;

        if (!owner) return EMPTY_CELL_VALUE;

        return `${getFullName(owner.fullName)} (${owner.email})`;
      },
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) =>
        data ? (
          <OpportunityOwnerCell
            canEdit={canEditDealOpportunity}
            dealId={dealId}
            row={data}
          />
        ) : null,
    };

  const rawColumns: ExportableColDef<DealTrackingLimitedPartnerBreakdown>[] = [
    limitedPartnerNameColumn,
    ...(isMutationLocked ? [] : [actionsColumn]),
    stageColumn,
    tierColumn,
    ndaColumn,
    nrlColumn,
    opportunityOwnerColumn,
  ];

  const { columns: exportColumns } = deriveExportColumns(rawColumns);
  const { columnDefs, renderColumnToggle } = useColumnToggle(
    toGridColumns(rawColumns),
  );

  const keyboardTriggerColIds = new Set<string>([
    TRACK_PROCESS_COL_IDS.nrl,
    TRACK_PROCESS_COL_IDS.opportunityOwner,
  ]);

  const onCellKeyDown = (
    event: CellKeyDownEvent<DealTrackingLimitedPartnerBreakdown>,
  ) => {
    if (!keyboardTriggerColIds.has(event.column.getColId())) return;

    const key = (event.event as KeyboardEvent | null)?.key;

    if (key !== "Enter" && key !== "F2") return;

    const cell = (event.event?.target as HTMLElement | null)?.closest(
      "[role='gridcell']",
    );

    const button = cell?.querySelector<HTMLButtonElement>("button");

    if (!button) return;

    // When the trigger button already holds focus (e.g. Radix restores focus to
    // it after the popover closes on Esc), the browser activates it natively on
    // Enter. Clicking it again here would toggle twice and flash the popover
    // open→closed, so only synthesize the click when focus is on the cell.
    if (button.contains(document.activeElement)) return;

    button.click();
  };

  return { columnDefs, renderColumnToggle, onCellKeyDown, exportColumns };
};
